import { DashboardLayoutContainer } from 'components/Layouts/DashboardLayout';
import styled from 'styled-components';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import React, { startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getUserListAction } from 'store/actions/userActions';
import { useNavigate } from 'react-router-dom';
import { Dropdown, Loading } from '@leapeasy/ui-kit';
import { ActiveLogTable } from 'components/Tables/ActiveLogTable';
import { useListQueryState } from 'hooks/useListQueryState';
import { useClampListPagination } from 'hooks/useClampListPagination';
import { getUserList } from 'store/sagas/APIs/user';
import { fetchAllPaginated } from 'utils/exportPagination';
import { useDropFilterActiveSummaryBar } from 'hooks/useDropFilterActiveSummaryBar';
import {
  buildActivityLogListFilterChips,
  parseActivityLogListFilterChipId,
} from 'utils/listTableActiveFilterChips';
import { ActivityLogDetailModal } from './ActivityLogDetailModal';
import { ROLE_DISPLAY_LABELS } from 'utils/entityTerminology';

const parseActivityLogFilters = (searchParams, _paramKey, fallback) => ({
  selectedUserStatus: searchParams.get('userStatus')
    ? searchParams
        .get('userStatus')
        .split(',')
        .filter(Boolean)
        .map((value) => value === 'true')
    : fallback.selectedUserStatus,
  selectedRole: searchParams.get('role')
    ? searchParams.get('role').split(',').filter(Boolean)
    : fallback.selectedRole,
});

const serializeActivityLogFilters = (filters) => ({
  userStatus:
    Array.isArray(filters.selectedUserStatus) && filters.selectedUserStatus.length > 0
      ? filters.selectedUserStatus.map((value) => (value ? 'true' : 'false')).join(',')
      : '',
  role:
    Array.isArray(filters.selectedRole) && filters.selectedRole.length > 0
      ? filters.selectedRole.join(',')
      : '',
});

const areArraysEqual = (a = [], b = []) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

const ROLE_FILTER_LABELS = {
  ll: ROLE_DISPLAY_LABELS.ll,
  vp: ROLE_DISPLAY_LABELS.vp,
  rm: ROLE_DISPLAY_LABELS.rm,
  pm: ROLE_DISPLAY_LABELS.pm,
};

export const ActivityLog = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    filter,
    setFilter,
    sortOptions,
    setSortOptions,
    pagination,
    setPagination,
    extraFilters,
    setExtraFilters,
  } = useListQueryState({
    defaultFilter: {
      options: {},
      searchText: '',
      searchPlaceholder: 'Search Active Logs..',
    },
    extraFiltersParam: 'filters',
    defaultExtraFilters: {
      selectedUserStatus: [],
      selectedRole: [],
    },
    parseExtraFilters: parseActivityLogFilters,
    serializeExtraFilters: serializeActivityLogFilters,
  });

  const [selectedUserStatus, setSelectedUserStatus] = useState([]);
  const [selectedRole, setSelectedRole] = useState([]);
  const [selectedUserStatusDraft, setSelectedUserStatusDraft] = useState([]);
  const [selectedRoleDraft, setSelectedRoleDraft] = useState([]);

  useEffect(() => {
    if (!extraFilters || typeof extraFilters !== 'object') return;
    const nextSelectedUserStatus = Array.isArray(extraFilters.selectedUserStatus)
      ? extraFilters.selectedUserStatus
      : [];
    const nextSelectedRole = Array.isArray(extraFilters.selectedRole)
      ? extraFilters.selectedRole
      : [];
    setSelectedUserStatus((prev) =>
      areArraysEqual(prev, nextSelectedUserStatus) ? prev : nextSelectedUserStatus,
    );
    setSelectedRole((prev) => (areArraysEqual(prev, nextSelectedRole) ? prev : nextSelectedRole));
    setSelectedUserStatusDraft((prev) =>
      areArraysEqual(prev, nextSelectedUserStatus) ? prev : nextSelectedUserStatus,
    );
    setSelectedRoleDraft((prev) =>
      areArraysEqual(prev, nextSelectedRole) ? prev : nextSelectedRole,
    );
  }, [extraFilters]);

  let activeLogs = useSelector((state) => state.getIn(['user', 'userlist']));
  const activeLogsJSON = activeLogs && activeLogs.toJS();
  useClampListPagination({
    pagination,
    setPagination,
    totalItems: Number(activeLogsJSON?.total) || 0,
    isLoading: Boolean(activeLogsJSON?.isLoading),
  });

  const [fullExportDatasetTotal, setFullExportDatasetTotal] = useState(undefined);
  const [selectedUserLog, setSelectedUserLog] = useState(null);

  const handleDispatch = useCallback(() => {
    dispatch(
      getUserListAction({
        filter: filter,
        offset: pagination.pageNumber * pagination.rowsPerPage,
        limit: pagination.rowsPerPage,
        sortOptions: sortOptions,

        selectedUserStatus: selectedUserStatus,
        selectedRole: selectedRole,
      }),
    );
  }, [
    dispatch,
    filter,
    pagination.pageNumber,
    pagination.rowsPerPage,
    sortOptions,
    selectedUserStatus,
    selectedRole,
  ]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await getUserList({
          filter: {
            options: {},
            searchText: '',
            searchPlaceholder: 'Search Active Logs..',
          },
          offset: 0,
          limit: 1,
          sortOptions,
          selectedUserStatus: [],
          selectedRole: [],
        });
        if (!cancelled && response?.total != null) {
          setFullExportDatasetTotal(Number(response.total));
        }
      } catch (e) {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sortOptions]);

  const handleRequestExportData = useCallback(async () => {
    const totalRecords = Number(activeLogsJSON?.total) || 0;
    if (totalRecords === 0) return [];
    try {
      return await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getUserList({
            filter,
            offset,
            limit,
            sortOptions,
            selectedUserStatus,
            selectedRole,
          }),
      });
    } catch (e) {
      throw new Error('Unable to export filtered activity logs right now.');
    }
  }, [activeLogsJSON?.total, filter, sortOptions, selectedUserStatus, selectedRole]);

  const handleRequestFullExportData = useCallback(async () => {
    const totalRecords = Number(fullExportDatasetTotal) || Number(activeLogsJSON?.total) || 0;
    if (totalRecords === 0) return [];
    try {
      return await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getUserList({
            filter: {
              options: {},
              searchText: '',
              searchPlaceholder: 'Search Active Logs..',
            },
            offset,
            limit,
            sortOptions,
            selectedUserStatus: [],
            selectedRole: [],
          }),
      });
    } catch (e) {
      throw new Error('Unable to export all activity logs right now.');
    }
  }, [fullExportDatasetTotal, activeLogsJSON?.total, sortOptions]);

  useEffect(() => {
    handleDispatch();
  }, [handleDispatch]);

  const onClickDetails = useCallback((user) => {
    if (!user) return;
    setSelectedUserLog(typeof user.toJS === 'function' ? user.toJS() : user);
  }, []);
  const resetFunc = useCallback(() => {
    setSelectedUserStatus([]);
    setSelectedRole([]);
    setSelectedUserStatusDraft([]);
    setSelectedRoleDraft([]);
    setExtraFilters({
      selectedUserStatus: [],
      selectedRole: [],
    });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [setExtraFilters, setPagination]);

  const applyFunc = useCallback(() => {
    setSelectedUserStatus(selectedUserStatusDraft);
    setSelectedRole(selectedRoleDraft);
    setExtraFilters({
      selectedUserStatus: selectedUserStatusDraft,
      selectedRole: selectedRoleDraft,
    });
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [selectedUserStatusDraft, selectedRoleDraft, setExtraFilters, setPagination]);

  const listFilterChips = useMemo(
    () =>
      buildActivityLogListFilterChips({
        selectedUserStatus,
        selectedRole,
        roleLabels: ROLE_FILTER_LABELS,
      }),
    [selectedUserStatus, selectedRole],
  );

  const handleRemoveActivityLogListFilterChip = useCallback(
    (chipId) => {
      const parsed = parseActivityLogListFilterChipId(chipId);
      if (!parsed) return;
      const bumpPage = () =>
        setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
      if (parsed.type === 'userStatus') {
        const next = selectedUserStatus.filter((s) => s !== parsed.value);
        setSelectedUserStatus(next);
        setSelectedUserStatusDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedUserStatus: next }));
        bumpPage();
        return;
      }
      if (parsed.type === 'role') {
        const next = selectedRole.filter((s) => s !== parsed.value);
        setSelectedRole(next);
        setSelectedRoleDraft(next);
        setExtraFilters((prev) => ({ ...prev, selectedRole: next }));
        bumpPage();
      }
    },
    [selectedUserStatus, selectedRole, setExtraFilters, setPagination],
  );

  const { dropFilterActiveSummary, dropFilterActiveCount } = useDropFilterActiveSummaryBar(
    listFilterChips,
    handleRemoveActivityLogListFilterChip,
    resetFunc,
  );

  return (
    <DashboardLayoutContainer>
      <ActivityLogDetailModal
        open={Boolean(selectedUserLog)}
        user={selectedUserLog}
        onClose={() => setSelectedUserLog(null)}
      />
      {activeLogs && activeLogsJSON.data ? (
        <React.Fragment>
          <CustomBox>
            {activeLogsJSON?.isLoading && (
              <LoadingEffect>
                <Loading size={'large'} />
              </LoadingEffect>
            )}
            <ActiveLogTable
              activeLogs={activeLogsJSON.data}
              onRequestExportData={handleRequestExportData}
              onRequestFullExportData={handleRequestFullExportData}
              exportFullDatasetTotal={fullExportDatasetTotal}
              dropFilterActiveSummary={dropFilterActiveSummary}
              dropFilterActiveCount={dropFilterActiveCount}
              filter={filter}
              onChangeFilter={setFilter}
              dropFilter={{
                component: (
                  <DropFilterContainer>
                    <Dropdown
                      onChange={(e) => {
                        setSelectedUserStatusDraft(e.target.value);
                      }}
                      options={[
                        // {
                        //   text: 'All Status',
                        //   value: null
                        // },
                        {
                          text: 'Offline',
                          value: false,
                        },
                        {
                          text: 'Online',
                          value: true,
                        },
                      ]}
                      value={selectedUserStatusDraft}
                      multiselect={true}
                      showSearch={false}
                      placeholder=""
                      label="User Status"
                      style={{
                        width: '100%',
                      }}
                    />
                    <Dropdown
                      onChange={(e) => {
                        setSelectedRoleDraft(e.target.value);
                      }}
                      options={[
                        // {
                        //   text: 'All Roles',
                        //   value: ''
                        // },
                        {
                          text: ROLE_DISPLAY_LABELS.ll,
                          value: 'll',
                        },
                        {
                          text: ROLE_DISPLAY_LABELS.vp,
                          value: 'vp',
                        },
                        {
                          text: ROLE_DISPLAY_LABELS.rm,
                          value: 'rm',
                        },
                        {
                          text: ROLE_DISPLAY_LABELS.pm,
                          value: 'pm',
                        },
                      ]}
                      value={selectedRoleDraft}
                      multiselect={true}
                      showSearch={false}
                      placeholder=""
                      label="Role"
                      style={{
                        width: '100%',
                      }}
                    />
                  </DropFilterContainer>
                ),
                resetFunc: resetFunc,
                applyFunc: applyFunc,
              }}
              pagination={{
                ...pagination,
                totalItems: activeLogsJSON.total,
                totalPages: Math.ceil(activeLogsJSON.total / pagination.rowsPerPage),
              }}
              onChangePagination={setPagination}
              sortOptions={sortOptions}
              onChangeSort={setSortOptions}
              onClickDetails={onClickDetails}
            />
          </CustomBox>
        </React.Fragment>
      ) : (
        <Loading size={'large'} />
      )}
    </DashboardLayoutContainer>
  );
};

const DropFilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  * {
    box-sizing: border-box;
  }
`;

const CustomBox = styled.div`
  position: relative;
`;

const LoadingEffect = styled.div`
  position: absolute;
  top: 35%;
  left: 45%;
  z-index: 1;
`;
